import { useCallback, useState } from 'react';
import { api } from 'services/api-client';
import { useToast } from '../../contexts/ToastContext';
import { IPlanGroup } from 'types/workout/plan-state';
import { parsedWorkoutToPlanGroups } from 'utils/parsedWorkoutToPlanGroups';

export interface ImportWorkoutAIResult {
  planName: string;
  personalizedNotes: string;
  groups: IPlanGroup[];
}

/**
 * Hook que orquesta la importacion de un workout via IA para el coach.
 *
 * - `parseFile(file)` envia un File (foto/xlsx/csv) al backend y devuelve el
 *   workout parseado + transformado al shape del editor (`IPlanGroup[]`).
 * - `parseText(text)` envia texto libre al backend.
 *
 * Ambos metodos manejan el toast de exito/error y retornan `null` cuando
 * algo falla (asi el caller no tiene que envolver en try/catch).
 */
export const useImportWorkoutAI = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState<boolean>(false);

  const transformAndNotify = useCallback((response: { data?: unknown } | unknown): ImportWorkoutAIResult => {
    // `apiClient.post` envuelve la respuesta en `{ data, ... }` (TransformInterceptor
    // del backend). Tomamos `data` si existe; sino asumimos que ya viene plano.
    const parsed = ((response as { data?: unknown }).data ?? response) as Parameters<
      typeof parsedWorkoutToPlanGroups
    >[0];
    const groups = parsedWorkoutToPlanGroups(parsed);
    return {
      planName: parsed.instanceName ?? 'Entrenamiento',
      personalizedNotes: parsed.personalizedNotes ?? '',
      groups
    };
  }, []);

  const parseFile = useCallback(
    async (file: File): Promise<ImportWorkoutAIResult | null> => {
      setLoading(true);
      try {
        const response = await api.workout.parseWorkoutFromAI({ file });
        const result = transformAndNotify(response);
        showToast('success', 'Workout importado', `${result.groups.length} grupo(s) detectados`);
        return result;
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Error desconocido';
        showToast('error', 'No se pudo importar', detail);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [showToast, transformAndNotify]
  );

  const parseText = useCallback(
    async (text: string): Promise<ImportWorkoutAIResult | null> => {
      if (!text || text.trim().length === 0) {
        showToast('warn', 'Texto vacio', 'Pega o escribe el contenido del workout');
        return null;
      }
      setLoading(true);
      try {
        const response = await api.workout.parseWorkoutFromAI({ text });
        const result = transformAndNotify(response);
        showToast('success', 'Workout importado', `${result.groups.length} grupo(s) detectados`);
        return result;
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Error desconocido';
        showToast('error', 'No se pudo importar', detail);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [showToast, transformAndNotify]
  );

  return { loading, parseFile, parseText };
};
